import React, { useEffect, useRef, useState } from 'react';
import {
  StreamVideoClient,
  StreamVideo as StreamVideoSDK,
  StreamCall as StreamCallSDK,
  useCallStateHooks,
  ParticipantView,
} from '@stream-io/video-react-sdk';
import { Mic, MicOff, Video, VideoOff, PhoneOff, Loader2, CircleDollarSign, Gift } from 'lucide-react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../../axiosInstance';
import { useVideoCall } from '../../contexts/VideoCallContext';
import { useApp } from '../../contexts/AppContext';
import CallTipModal from './CallTipModal';
import CallGiftPicker from './CallGiftPicker';
import CallEventOverlay from './CallEventOverlay';

const fullscreenVideoStyles = `
  /* Inner SDK elements fill whatever their wrapper is sized to.
     DO NOT put .vcr-participant here — it would override the PIP's fixed 96x140. */
  .vcr-participant .str-video__participant-view,
  .vcr-participant video {
    width: 100% !important;
    height: 100% !important;
    object-fit: cover !important;
    position: absolute !important;
    top: 0 !important;
    left: 0 !important;
  }
  .vcr-participant .str-video__participant-details,
  .vcr-participant .str-video__participant-view__mute-indicator,
  .vcr-participant .str-video__speech-indicator {
    display: none !important;
  }
  /* PIP: hard lock the dimensions at the wrapper level so nothing can blow it up */
  .vcr-pip {
    width: 96px !important;
    height: 140px !important;
    max-width: 96px !important;
    max-height: 140px !important;
    overflow: hidden !important;
  }
  .vcr-pip .str-video__participant-view,
  .vcr-pip video { border-radius: 18px !important; }
  .vcr-main .str-video__participant-view,
  .vcr-main video { border-radius: 0 !important; }
`;

const formatDuration = (seconds) => {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

// PIP dimensions — WhatsApp-sized 9:16 portrait thumbnail
const PIP_WIDTH = 96;
const PIP_HEIGHT = 140;
const PIP_MARGIN = 16;
// Reserve space at top (header) and bottom (call controls) so the PIP
// can't snap over them
const PIP_TOP_INSET = 72;
const PIP_BOTTOM_INSET = 140;

// Map a corner code → {left,top} coordinates within the viewport
const cornerPosition = (corner) => {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const left = corner.endsWith('l')
    ? PIP_MARGIN
    : vw - PIP_WIDTH - PIP_MARGIN;
  const top = corner.startsWith('t')
    ? PIP_MARGIN + PIP_TOP_INSET
    : vh - PIP_HEIGHT - PIP_MARGIN - PIP_BOTTOM_INSET;
  return { left, top };
};

// Given a free-drag position, pick the nearest of 4 corners
const nearestCorner = (x, y) => {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const cx = x + PIP_WIDTH / 2;
  const cy = y + PIP_HEIGHT / 2;
  const v = cy < vh / 2 ? 't' : 'b';
  const h = cx < vw / 2 ? 'l' : 'r';
  return `${v}${h}`;
};

const clampToViewport = (x, y) => ({
  x: Math.max(PIP_MARGIN, Math.min(window.innerWidth - PIP_WIDTH - PIP_MARGIN, x)),
  y: Math.max(PIP_MARGIN + PIP_TOP_INSET, Math.min(window.innerHeight - PIP_HEIGHT - PIP_MARGIN - PIP_BOTTOM_INSET, y)),
});

const CallStage = ({ onEnd, priceLabel, recipientName, callId, otherUser, canTipOther }) => {
  const { t } = useTranslation('livestream');
  const { useLocalParticipant, useRemoteParticipants, useMicrophoneState, useCameraState } = useCallStateHooks();
  const { updateBalance } = useApp();
  const currentUserId = useSelector((s) => s.auth?.auth?.user?.id ?? s.auth?.user?.id);
  const localParticipant = useLocalParticipant();
  const remote = useRemoteParticipants()?.[0];
  const { microphone, isMute: micMuted } = useMicrophoneState();
  const { camera, isMute: camMuted } = useCameraState();
  const [elapsed, setElapsed] = useState(0);

  // PIP state — which corner it's snapped to, and whether local/remote are swapped
  const [corner, setCorner] = useState('br'); // bottom-right default
  const [swapped, setSwapped] = useState(false);
  const [dragPos, setDragPos] = useState(null); // {x,y} while dragging, null when idle
  const dragRef = useRef(null); // holds mutable drag state during a gesture
  const pipRef = useRef(null);

  // Tip/gift UI
  const [showTipModal, setShowTipModal] = useState(false);
  const [showGiftPicker, setShowGiftPicker] = useState(false);

  // Events polling (tips + gifts fired during this call — shown to both parties)
  const [events, setEvents] = useState([]);
  const sinceIdRef = useRef(0);
  useEffect(() => {
    if (!callId) return;
    let active = true;
    const poll = async () => {
      try {
        const res = await axiosInstance.get(`/calls/${callId}/events`, {
          params: { since_id: sinceIdRef.current },
        });
        if (!active || !res.data.success) return;
        const newEvents = res.data.data || [];
        if (newEvents.length > 0) {
          sinceIdRef.current = res.data.latest_id || sinceIdRef.current;
          setEvents((prev) => [...prev, ...newEvents]);

          // Real-time wallet update for the RECIPIENT (creator being tipped/gifted)
          if (currentUserId) {
            const myEarnings = newEvents
              .filter((e) => Number(e.recipient_id) === Number(currentUserId))
              .reduce((sum, e) => sum + Number(e.recipient_earnings || 0), 0);
            if (myEarnings > 0) updateBalance(myEarnings);
          }
        }
      } catch {}
    };
    poll();
    const timer = setInterval(poll, 2000);
    return () => { active = false; clearInterval(timer); };
  }, [callId, currentUserId, updateBalance]);

  useEffect(() => {
    const tick = setInterval(() => setElapsed((s) => s + 1), 1000);
    return () => clearInterval(tick);
  }, []);

  const toggleMic = () => micMuted ? microphone.enable() : microphone.disable();
  const toggleCam = () => camMuted ? camera.enable() : camera.disable();

  // ── PIP drag handlers (works for mouse + touch via pointer events) ──
  const handlePointerDown = (e) => {
    if (!pipRef.current) return;
    const rect = pipRef.current.getBoundingClientRect();
    dragRef.current = {
      offsetX: e.clientX - rect.left,
      offsetY: e.clientY - rect.top,
      startX: e.clientX,
      startY: e.clientY,
      maxMoved: 0,
    };
    try { pipRef.current.setPointerCapture(e.pointerId); } catch {}
    setDragPos({ x: rect.left, y: rect.top });
  };

  const handlePointerMove = (e) => {
    if (!dragRef.current) return;
    const x = e.clientX - dragRef.current.offsetX;
    const y = e.clientY - dragRef.current.offsetY;
    const moved = Math.hypot(e.clientX - dragRef.current.startX, e.clientY - dragRef.current.startY);
    if (moved > dragRef.current.maxMoved) dragRef.current.maxMoved = moved;
    const clamped = clampToViewport(x, y);
    setDragPos({ x: clamped.x, y: clamped.y });
  };

  const handlePointerUp = (e) => {
    if (!dragRef.current) return;
    const maxMoved = dragRef.current.maxMoved;
    const pos = dragPos;
    try { pipRef.current?.releasePointerCapture(e.pointerId); } catch {}
    dragRef.current = null;
    setDragPos(null);

    if (maxMoved < 6) {
      // Treat as a tap → swap the streams
      setSwapped((s) => !s);
    } else if (pos) {
      // Dropped — snap to nearest corner
      setCorner(nearestCorner(pos.x, pos.y));
    }
  };

  // Re-snap on window resize so the PIP stays on screen in the same corner
  useEffect(() => {
    const handler = () => setCorner((c) => c); // force re-render to recompute
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  // Main participant fills the whole stage; PIP is the other one (draggable).
  const mainParticipant = swapped ? localParticipant : remote;
  const pipParticipant = swapped ? remote : localParticipant;

  const pipStyle = dragPos
    ? { left: dragPos.x, top: dragPos.y, transition: 'none' }
    : { ...cornerPosition(corner), transition: 'left 0.25s ease, top 0.25s ease' };

  return (
    <div className="fixed inset-0 bg-black z-[200] flex flex-col select-none">
      <style>{fullscreenVideoStyles}</style>

      {/* Top bar */}
      <div className="absolute top-0 left-0 right-0 z-10 px-4 py-3 flex items-center justify-between text-white bg-gradient-to-b from-black/60 to-transparent">
        <div>
          <p className="text-sm font-semibold">{recipientName}</p>
          <p className="text-[11px] opacity-70">{priceLabel} · {formatDuration(elapsed)}</p>
        </div>
      </div>

      {/* Main stream (full-screen background) */}
      <div className="flex-1 relative bg-black">
        {mainParticipant ? (
          <div className="vcr-participant vcr-main absolute inset-0">
            <ParticipantView participant={mainParticipant} />
          </div>
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-white">
            <div className="text-center">
              <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2 opacity-70" />
              <p className="text-sm opacity-80">{t('videoCall.room.connecting')}</p>
            </div>
          </div>
        )}

        {/* Tip / gift floating overlay */}
        <CallEventOverlay events={events} />

        {/* Draggable PIP (other participant) — tap to swap, drag to reposition */}
        {pipParticipant && (
          <div
            ref={pipRef}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerUp}
            className="vcr-participant vcr-pip fixed rounded-[18px] overflow-hidden ring-1 ring-white/25 shadow-[0_8px_24px_rgba(0,0,0,0.4)] cursor-grab active:cursor-grabbing touch-none"
            style={{
              width: PIP_WIDTH,
              height: PIP_HEIGHT,
              zIndex: 20,
              ...pipStyle,
            }}
            title="Drag to move · Tap to swap"
          >
            <ParticipantView participant={pipParticipant} />
            {/* Invisible click shield so the SDK's inner video doesn't capture pointer events */}
            <div className="absolute inset-0" style={{ pointerEvents: 'none' }} />
          </div>
        )}
      </div>

      {/* Bottom controls */}
      <div className="absolute bottom-0 left-0 right-0 pb-10 pt-8 px-6 flex items-center justify-center gap-3 bg-gradient-to-t from-black/60 to-transparent z-10">
        <button
          onClick={toggleMic}
          title={micMuted ? t('videoCall.room.unmute') : t('videoCall.room.mute')}
          className={`w-[52px] h-[52px] rounded-full flex items-center justify-center transition-colors ${micMuted ? 'bg-white text-gray-900' : 'bg-white/15 text-white hover:bg-white/25'}`}
        >
          {micMuted ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
        </button>
        <button
          onClick={toggleCam}
          title={camMuted ? t('videoCall.room.enableCamera') : t('videoCall.room.disableCamera')}
          className={`w-[52px] h-[52px] rounded-full flex items-center justify-center transition-colors ${camMuted ? 'bg-white text-gray-900' : 'bg-white/15 text-white hover:bg-white/25'}`}
        >
          {camMuted ? <VideoOff className="w-5 h-5" /> : <Video className="w-5 h-5" />}
        </button>
        <button
          onClick={onEnd}
          title={t('videoCall.room.endCall')}
          className="w-16 h-16 rounded-full bg-red-500 hover:bg-red-600 flex items-center justify-center shadow-2xl"
        >
          <PhoneOff className="w-7 h-7 text-white" />
        </button>
        {canTipOther && (
          <>
            <button
              onClick={() => setShowGiftPicker(true)}
              title={t('videoCall.room.sendGift')}
              className="w-[52px] h-[52px] rounded-full bg-white/15 hover:bg-white/25 text-white flex items-center justify-center transition-colors"
            >
              <Gift className="w-5 h-5" />
            </button>
            <button
              onClick={() => setShowTipModal(true)}
              title={t('videoCall.room.sendTip')}
              className="w-[52px] h-[52px] rounded-full text-white flex items-center justify-center transition-colors"
              style={{ background: 'linear-gradient(135deg, var(--color-primary-500), var(--color-primary-700))' }}
            >
              <CircleDollarSign className="w-5 h-5" />
            </button>
          </>
        )}
      </div>

      <CallTipModal
        isOpen={showTipModal}
        onClose={() => setShowTipModal(false)}
        callId={callId}
        recipient={otherUser}
      />
      <CallGiftPicker
        isOpen={showGiftPicker}
        onClose={() => setShowGiftPicker(false)}
        callId={callId}
        recipient={otherUser}
      />
    </div>
  );
};

const VideoCallRoom = () => {
  const { t } = useTranslation('livestream');
  const { currentCall, endCall } = useVideoCall();
  const [client, setClient] = useState(null);
  const [call, setCall] = useState(null);
  const [error, setError] = useState(null);
  const setupDone = useRef(false);

  const shouldShow = currentCall && ['accepted', 'active'].includes(currentCall.status);

  useEffect(() => {
    if (!shouldShow || setupDone.current) return;
    setupDone.current = true;

    (async () => {
      try {
        const tokenRes = await axiosInstance.get(`/calls/${currentCall.id}/token`);
        if (!tokenRes.data.success) throw new Error('Failed to get token');

        const { token, api_key, call_id, user_id } = tokenRes.data.data;

        const otherUser = currentCall.my_role === 'caller' ? currentCall.recipient : currentCall.caller;

        const user = {
          id: String(user_id),
          name: otherUser ? (otherUser.display_name || otherUser.username) : 'Me',
        };

        const newClient = new StreamVideoClient({ apiKey: api_key, user, token });
        const newCall = newClient.call('default', call_id);
        await newCall.join({ create: false });

        setClient(newClient);
        setCall(newCall);
      } catch (e) {
        console.error('Failed to join video call', e);
        setError(t('videoCall.room.failedToJoin'));
      }
    })();
  }, [shouldShow, currentCall, t]);

  // Cleanup when call ends
  useEffect(() => {
    if (!shouldShow && call) {
      call.leave().catch(() => {});
      if (client) client.disconnectUser().catch(() => {});
      setCall(null);
      setClient(null);
      setupDone.current = false;
    }
  }, [shouldShow, call, client]);

  if (!shouldShow) return null;

  if (error) {
    return (
      <div className="fixed inset-0 z-[200] bg-black flex items-center justify-center text-white">
        <div className="text-center">
          <p className="text-sm mb-4">{error}</p>
          <button
            onClick={endCall}
            className="px-4 py-2 rounded-full bg-red-500 hover:bg-red-600 text-sm font-medium"
          >
            {t('videoCall.room.endCall')}
          </button>
        </div>
      </div>
    );
  }

  if (!client || !call) {
    return (
      <div className="fixed inset-0 z-[200] bg-black flex items-center justify-center text-white">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-3 opacity-70" />
          <p className="text-sm opacity-80">{t('videoCall.room.connectingToCall')}</p>
        </div>
      </div>
    );
  }

  const other = currentCall.my_role === 'caller' ? currentCall.recipient : currentCall.caller;
  const priceLabel =
    currentCall.pricing_mode === 'free' ? t('videoCall.common.freeCall') :
    currentCall.pricing_mode === 'per_minute'
      ? `$${Number(currentCall.price).toFixed(2)}/min`
      : `$${Number(currentCall.price).toFixed(2)} ${t('videoCall.room.flat')}`;

  // Only creators can receive tips/gifts — hide the buttons if the other party isn't one
  const canTipOther = !!(other && other.is_creator);

  return (
    <StreamVideoSDK client={client}>
      <StreamCallSDK call={call}>
        <CallStage
          onEnd={endCall}
          priceLabel={priceLabel}
          recipientName={other?.display_name || other?.username || t('videoCall.room.call')}
          callId={currentCall.id}
          otherUser={other}
          canTipOther={canTipOther}
        />
      </StreamCallSDK>
    </StreamVideoSDK>
  );
};

export default VideoCallRoom;
